import useHasPermissions from '@/components/access-control/useHasPermissions.hook';
import Badge from '@/components/badge';
import Card from '@/components/card';
import { StpResultDto } from '@/models/StpResultDTO.model';
import { FeatureLevelPermissions } from '@/models/UserPermissions.model';
import ParticipantService from '@/services/Participant.service';
import formatters from '@/utils/Formatters';
import { Replay } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CodeIcon from '@mui/icons-material/Code';
import CodeOffIcon from '@mui/icons-material/CodeOff';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Collapse,
  Divider,
  Unstable_Grid2 as Grid,
  Stack,
  styled,
  Typography
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useToggle } from '@vestwell-frontend/hooks';

import _ from 'lodash';
import { FC, useCallback, useEffect, useMemo } from 'react';

type DistributionStpResultCardProps = {
  stpResult: StpResultDto[];
  distributionId: number;
  distributionType: 'LOAN' | 'WITHDRAWAL';
  enableReplay?: boolean;
};

const StyledGridCell = styled(Grid)(props => ({
  ':not(:first-child)': {
    paddingLeft: props.theme.spacing(2)
  },
  ':not(:last-child)': {
    borderRight: `1px solid ${props.theme.palette.grey[300]}`
  }
}));

export const DistributionStpResultCard: FC<
  DistributionStpResultCardProps
> = props => {
  const [showDetails, toggleShowDetails] = useToggle();
  const [showStpRunning, toggleShowStpRunning] = useToggle();

  const replayPerms = useHasPermissions({
    requiresOneOf: [
      FeatureLevelPermissions.WRITE_WITHDRAWALS_ACTION,
      FeatureLevelPermissions.WRITE_FRAUD
    ]
  });

  const enableReplay = useMemo(() => {
    return (
      props.enableReplay &&
      props.distributionType === 'WITHDRAWAL' &&
      replayPerms.isAllowed
    );
  }, [props, replayPerms]);

  const runStpMutation = useMutation(
    ['ParticipantService.runWithdrawalStp', props.distributionId],
    () => {
      return props.distributionType == 'WITHDRAWAL'
        ? ParticipantService.runWithdrawalStp(props.distributionId)
        : Promise.resolve();
    }
  );

  const onRunStp = useCallback(() => {
    toggleShowStpRunning(true);
    runStpMutation.mutate();
  }, []);

  useEffect(() => {
    toggleShowStpRunning(false);
  }, [props.stpResult]);

  return (
    <Card>
      <CardHeader
        title={
          <Grid container>
            {props.stpResult.map(stp => (
              <StyledGridCell key={stp.ruleType} xs={6}>
                <Stack>
                  <Typography variant='h6'>
                    {stp.ruleType === 'FRAUD'
                      ? 'Fraud Checks'
                      : 'Distribution Checks'}
                  </Typography>
                  <Stack direction='row' spacing={2}>
                    <Badge color={stp.passed ? 'success' : 'warning'}>
                      {stp.passed ? 'Passed STP' : 'Failed STP'}
                    </Badge>
                    <Typography>
                      {formatters.formatFromIsoDate(stp.updatedAt)}
                    </Typography>
                  </Stack>
                </Stack>
              </StyledGridCell>
            ))}
            {!props.stpResult.length && (
              <StyledGridCell xs={12}>
                <Typography variant='h6'>
                  STP did not run for this
                  {_.lowerCase(props.distributionType)}
                </Typography>
              </StyledGridCell>
            )}
          </Grid>
        }
      />
      {!!props.stpResult.length && (
        <Collapse in={showDetails} timeout='auto' unmountOnExit>
          <Divider />
          <CardContent>
            <Grid container>
              {props.stpResult.map(stp => (
                <StyledGridCell key={stp.ruleType} xs={6}>
                  {stp.result.map(result => (
                    <Stack
                      direction='row'
                      key={result.ruleName}
                      pb={2}
                      spacing={1}>
                      {result.passed ? (
                        <CheckCircleIcon color='success' />
                      ) : (
                        <ErrorIcon color='error' />
                      )}
                      <Stack>
                        <Typography variant='subtitle2'>
                          {formatters.displayCase(result.ruleName)}
                        </Typography>
                        <Typography variant='body2'>{result.reason}</Typography>
                      </Stack>
                    </Stack>
                  ))}
                </StyledGridCell>
              ))}
            </Grid>
          </CardContent>
        </Collapse>
      )}
      {(props.stpResult.length > 0 || enableReplay) && (
        <>
          <Divider />
          <CardActions>
            {!!props.stpResult.length && (
              <Button
                onClick={toggleShowDetails}
                startIcon={
                  showDetails ? <CodeOffIcon /> : <CodeIcon />
                }>{`${showDetails ? 'Hide' : 'Show'} Details`}</Button>
            )}
            {enableReplay && !showStpRunning && (
              <Button onClick={onRunStp} startIcon={<Replay />}>
                {props.stpResult.length ? 'Replay' : 'Run'} STP
              </Button>
            )}
            {showStpRunning && <CircularProgress size={15} />}
          </CardActions>
        </>
      )}
    </Card>
  );
};

DistributionStpResultCard.displayName = 'DistributionStpResultCard';
